import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const Program = () => {
  const { t } = useLang();
  const P = t.program;

  const handleApply = () => {
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="program"
      data-testid="program-section"
      className="py-24 md:py-32 bg-navy-50/40"
    >
      <div className="container-vel">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <div className="overline mb-5" data-testid="program-overline">
              {P.overline}
            </div>
            <h2
              data-testid="program-heading"
              className="font-heading text-4xl sm:text-5xl lg:text-6xl text-navy font-light leading-[1.05] tracking-tight"
            >
              {P.headline}.
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-5 font-body text-base sm:text-lg text-ink-muted leading-relaxed font-light"
          >
            {P.intro}
          </motion.p>
        </div>

        <ol
          data-testid="journey-steps"
          className="relative border-l border-navy/15 ml-3 md:ml-6 pl-6 md:pl-10 space-y-10"
        >
          {P.steps.map((step, idx) => (
            <motion.li
              key={step.number}
              data-testid={`journey-step-${step.number}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: idx * 0.05 }}
              className="relative bg-white border border-navy/10 p-8 md:p-10 hover:border-coral/60 transition-colors duration-500 group"
            >
              <span className="absolute -left-[34px] md:-left-[50px] top-9 w-4 h-4 rounded-full bg-coral ring-4 ring-navy-50/40 group-hover:scale-110 transition-transform duration-300" />

              <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-3">
                <div>
                  <div className="font-body text-xs font-semibold tracking-[0.22em] text-coral">
                    {P.stepWord} {step.number}
                  </div>
                  <h3 className="mt-3 font-heading text-2xl sm:text-3xl text-navy font-medium leading-tight">
                    {step.title}
                  </h3>
                </div>
                <p className="font-heading italic text-base sm:text-lg text-navy/70">
                  {step.caption}
                </p>
              </div>

              <p className="mt-5 font-body text-sm sm:text-base text-ink-muted leading-relaxed font-light">
                {step.description}
              </p>
            </motion.li>
          ))}
        </ol>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-navy text-white p-10 md:p-14">
          <div className="lg:col-span-8">
            <div className="overline mb-3" style={{ color: "#FF6B35" }}>
              {P.ctaBanner.overline}
            </div>
            <p className="font-heading text-2xl sm:text-3xl lg:text-4xl leading-snug font-light">
              {P.ctaBanner.line}
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <button
              data-testid="program-cta-button"
              onClick={handleApply}
              className="group inline-flex items-center gap-2 bg-coral hover:bg-coral-600 text-white font-body text-sm font-medium tracking-wide px-7 py-4 rounded-sm transition-all duration-300 hover:-translate-y-0.5"
            >
              {P.ctaBanner.cta}
              <ArrowRight
                size={16}
                strokeWidth={1.75}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Program;
